/** The platform's own voice — German speech synthesised on our server.
 *
 * Runs on our container rather than a paid cloud voice, which is why there
 * is no quota here and no credit cost. The point of preferring it over the
 * browser's built-in synthesis is consistency: `speechSynthesis` gives a
 * different voice on every device, and on an iPhone it is whatever Apple
 * happens to have installed.
 */
import { API_BASE, getTokens, request } from "./http";

export type VoiceId = "female" | "male";

export interface VoiceStatus {
  /** False on a deployment with no voice models — fall back to the browser. */
  configured: boolean;
  voices: VoiceId[];
  preferred: VoiceId;
  max_chars: number;
}

export function voiceStatus(): Promise<VoiceStatus> {
  return request("/speech/voices");
}

/** Synthesise `text` and return the audio.
 *
 * Not `request()`: that helper parses the body as JSON and this one is an
 * MP3. It reuses API_BASE and the same bearer token so the two cannot drift
 * apart on where the API lives or how it is authenticated.
 */
export async function say(text: string, voice?: VoiceId | null): Promise<Blob> {
  const { access } = getTokens();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (access) headers.Authorization = `Bearer ${access}`;

  const res = await fetch(`${API_BASE}/speech/say`, {
    method: "POST",
    headers,
    body: JSON.stringify({ text, voice: voice ?? null }),
  });
  // Every failure is the same to a caller: it could not speak, so use the
  // browser's voice instead. 503 (no models) is the expected one.
  if (!res.ok) throw new Error(`speech unavailable: ${res.status}`);
  return res.blob();
}
